import { Link } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { type Route } from './+types/tos.ts'

export const meta: Route.MetaFunction = () => {
	return [
		{ title: 'Terms of Service - MyBillingsMethod' },
		{
			name: 'description',
			content:
				'MyBillingsMethod terms of service. Read our subscription terms, refund policy, and user agreement for our Mac fertility tracking app.',
		},
	]
}

export default function TermsOfServiceRoute() {
	const lastUpdated = 'December 15, 2025'

	return (
		<div className="bg-white">
			{/* Navigation */}
			<nav className="sticky top-0 z-40 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
				<div className="mx-auto max-w-5xl px-6 py-4">
					<div className="flex items-center justify-between">
						<Link to="/" className="flex items-center space-x-2">
							<Icon name="lock-closed" className="h-6 w-6 text-purple-600" />
							<span className="text-lg font-semibold text-gray-900">
								MyBillingsMethod
							</span>
						</Link>
						<Link
							to="/"
							className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
						>
							<Icon name="arrow-left" className="mr-2 h-4 w-4" />
							Back to Home
						</Link>
					</div>
				</div>
			</nav>

			{/* Header */}
			<div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
				<div className="mx-auto max-w-4xl px-6 py-16">
					<h1 className="mb-4 text-4xl font-bold">Terms of Service</h1>
					<p className="text-lg text-purple-100">
						Clear terms. Fair policies. No surprises.
					</p>
					<p className="mt-4 text-sm text-purple-200">
						Last updated: {lastUpdated}
					</p>
				</div>
			</div>

			{/* Content */}
			<div className="mx-auto max-w-4xl px-6 py-12">
				{/* 1. Acceptance of Terms */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						1. Acceptance of Terms
					</h2>
					<p className="text-gray-700">
						By accessing or using MyBillingsMethod ("the Service"), you agree to
						be bound by these Terms of Service. If you do not agree to these
						terms, do not use the Service.
					</p>
					<p className="mt-4 text-gray-700">
						These terms constitute a legally binding agreement between you and
						CHRIDAM LTD, a company registered in the United Kingdom.
					</p>
				</section>

				{/* 2. Description of Service */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						2. Description of Service
					</h2>
					<p className="mb-4 text-gray-700">
						MyBillingsMethod is a Mac desktop application that helps users track
						the Billings Ovulation Method. The Service includes:
					</p>
					<ul className="ml-6 space-y-2 text-gray-700">
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Local fertility observation tracking</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>AI-powered pattern recognition (runs locally on your Mac)</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Cycle charting and visualization</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Export functionality for instructor consultations</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Partner features (Couple plan only)</span>
						</li>
					</ul>
					<p className="mt-4 text-gray-700">
						<strong>Critical:</strong> All fertility data is stored exclusively on
						your Mac. We do not have cloud servers for fertility data.
					</p>
				</section>

				{/* 3. Eligibility */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						3. Eligibility
					</h2>
					<p className="text-gray-700">
						You must be at least 18 years old to use MyBillingsMethod. By using
						the Service, you represent and warrant that you meet this age
						requirement.
					</p>
				</section>

				{/* 4. Account Registration */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						4. Account Registration
					</h2>
					<p className="mb-4 text-gray-700">
						To access paid features, you must create an account. You agree to:
					</p>
					<ul className="ml-6 space-y-2 text-gray-700">
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Provide accurate and complete registration information</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Maintain the security of your password</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								Notify us immediately of any unauthorized use of your account
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								Accept responsibility for all activities under your account
							</span>
						</li>
					</ul>
				</section>

				{/* 5. Subscription Plans and Pricing */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						5. Subscription Plans and Pricing
					</h2>

					<div className="space-y-6">
						<div>
							<h3 className="mb-3 text-xl font-semibold text-gray-900">
								5.1 Available Plans
							</h3>
							<div className="space-y-4">
								<div className="rounded-lg border border-gray-200 p-4">
									<h4 className="font-semibold text-gray-900">
										Try It Free - £0
									</h4>
									<p className="text-sm text-gray-600">
										Basic charting functionality. No payment required.
									</p>
								</div>
								<div className="rounded-lg border border-gray-200 p-4">
									<h4 className="font-semibold text-gray-900">
										Practitioner - £39/year
									</h4>
									<p className="text-sm text-gray-600">
										AI insights, instructor exports, advanced features.
									</p>
									<p className="mt-2 text-xs text-purple-600">
										Beta Pricing: £29/year for the first 100 users (locks in
										forever)
									</p>
								</div>
								<div className="rounded-lg border border-gray-200 p-4">
									<h4 className="font-semibold text-gray-900">
										Couple - £59/year
									</h4>
									<p className="text-sm text-gray-600">
										All Practitioner features plus partner access and couple
										features.
									</p>
								</div>
							</div>
						</div>

						<div>
							<h3 className="mb-3 text-xl font-semibold text-gray-900">
								5.2 Billing
							</h3>
							<ul className="ml-6 space-y-2 text-gray-700">
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>
										Subscriptions are billed annually in British Pounds (£)
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>
										Payment is processed by Stripe (we never see your card
										details)
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>
										Your subscription will automatically renew unless cancelled
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>
										Beta pricing (£29/year) locks in forever for the first 100
										users
									</span>
								</li>
							</ul>
						</div>

						<div>
							<h3 className="mb-3 text-xl font-semibold text-gray-900">
								5.3 Price Changes
							</h3>
							<p className="text-gray-700">
								We may change subscription prices with 30 days notice. Price
								changes do not affect your current billing period. If you
								secured beta pricing (£29/year), that rate is locked in for life.
							</p>
						</div>
					</div>
				</section>

				{/* 6. Cancellation and Refunds */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						6. Cancellation and Refunds
					</h2>

					<div className="space-y-4">
						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Cancellation
							</h3>
							<p className="text-gray-700">
								You can cancel your subscription at any time from your account
								settings. Cancellation takes effect at the end of your current
								billing period. You will retain access until then.
							</p>
						</div>

						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Pre-Launch Refunds
							</h3>
							<p className="text-gray-700">
								If you reserved beta access before our April 2026 launch, you
								are entitled to a full refund at any time before launch. No
								questions asked.
							</p>
						</div>

						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Post-Launch Refunds
							</h3>
							<p className="text-gray-700">
								After the app launches, you may request a refund within 30 days
								of purchase. Contact hello@mybillingsmethod.com with your request.
								We process refunds within 5-10 business days.
							</p>
						</div>

						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								No Refunds After 30 Days
							</h3>
							<p className="text-gray-700">
								After 30 days from purchase, we do not offer refunds. Please try
								the free version before upgrading to ensure the Service meets
								your needs.
							</p>
						</div>
					</div>
				</section>

				{/* 7. Acceptable Use */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						7. Acceptable Use
					</h2>
					<p className="mb-4 text-gray-700">You agree NOT to:</p>
					<ul className="ml-6 space-y-2 text-gray-700">
						<li className="flex items-start">
							<span className="mr-2 text-red-600">✗</span>
							<span>
								Reverse engineer, decompile, or disassemble the application
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-red-600">✗</span>
							<span>Attempt to gain unauthorized access to our systems</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-red-600">✗</span>
							<span>
								Use the Service for any illegal or unauthorized purpose
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-red-600">✗</span>
							<span>
								Interfere with or disrupt the integrity of the Service
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-red-600">✗</span>
							<span>Share your account credentials with others</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-red-600">✗</span>
							<span>
								Redistribute or resell the Service without written permission
							</span>
						</li>
					</ul>
				</section>

				{/* 8. Intellectual Property */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						8. Intellectual Property
					</h2>

					<div className="space-y-4">
						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Your Data
							</h3>
							<p className="text-gray-700">
								You retain all rights to your fertility data. It's yours.
								Forever. We cannot access it because it never leaves your Mac.
							</p>
						</div>

						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Our Application
							</h3>
							<p className="text-gray-700">
								The MyBillingsMethod application, including all code, design,
								graphics, and documentation, is owned by CHRIDAM LTD and
								protected by copyright and other intellectual property laws.
							</p>
						</div>

						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								License
							</h3>
							<p className="text-gray-700">
								We grant you a limited, non-exclusive, non-transferable license
								to use the application for your personal use in accordance with
								these Terms.
							</p>
						</div>
					</div>
				</section>

				{/* 9. Medical Disclaimer - CRITICAL */}
				<section className="mb-12">
					<div className="rounded-xl border-2 border-red-200 bg-red-50 p-8">
						<h2 className="mb-4 flex items-center text-3xl font-bold text-red-900">
							<Icon name="exclamation-triangle" className="mr-3 h-8 w-8" />
							9. Medical Disclaimer
						</h2>
						<div className="space-y-4 text-gray-700">
							<p>
								<strong>READ THIS CAREFULLY:</strong>
							</p>
							<p>
								MyBillingsMethod is a fertility tracking tool, NOT a medical
								device. It is NOT a substitute for professional medical advice,
								diagnosis, or treatment.
							</p>
							<ul className="ml-6 space-y-2">
								<li className="flex items-start">
									<span className="mr-2 text-red-600">•</span>
									<span>
										This app does not prevent pregnancy or sexually transmitted
										infections
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">•</span>
									<span>
										The AI insights are educational tools, not medical
										diagnoses
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">•</span>
									<span>
										You must be trained by a qualified Billings Method
										instructor before relying on this method
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">•</span>
									<span>
										Always consult a healthcare professional for medical advice
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">•</span>
									<span>
										We are not responsible for pregnancy, health outcomes, or
										medical decisions made using this app
									</span>
								</li>
							</ul>
							<p className="mt-4 font-semibold">
								By using this Service, you acknowledge and accept these
								limitations.
							</p>
						</div>
					</div>
				</section>

				{/* 10. Limitation of Liability */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						10. Limitation of Liability
					</h2>
					<div className="space-y-4 text-gray-700">
						<p>
							TO THE MAXIMUM EXTENT PERMITTED BY LAW, CHRIDAM LTD SHALL
							NOT BE LIABLE FOR:
						</p>
						<ul className="ml-6 space-y-2">
							<li className="flex items-start">
								<span className="mr-2 text-purple-600">•</span>
								<span>
									Any indirect, incidental, special, or consequential damages
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 text-purple-600">•</span>
								<span>Loss of data stored locally on your Mac</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 text-purple-600">•</span>
								<span>
									Medical, health, or pregnancy outcomes related to use of the
									Service
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 text-purple-600">•</span>
								<span>
									Decisions made based on AI insights or pattern recognition
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 text-purple-600">•</span>
								<span>Service interruptions or technical issues</span>
							</li>
						</ul>
						<p className="mt-4">
							OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE
							SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM.
						</p>
						<p className="mt-4 text-sm">
							Some jurisdictions do not allow the exclusion of certain
							warranties or limitations on liability. In such cases, our
							liability will be limited to the maximum extent permitted by law.
						</p>
					</div>
				</section>

				{/* 11. Termination */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						11. Termination
					</h2>
					<div className="space-y-4 text-gray-700">
						<p>
							We may suspend or terminate your account if you violate these
							Terms. You may terminate your account at any time by cancelling
							your subscription.
						</p>
						<p>
							Upon termination, your right to use the Service ceases
							immediately. Your locally stored fertility data remains on your Mac
							- we cannot and will not access or delete it.
						</p>
					</div>
				</section>

				{/* 12. Governing Law */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						12. Governing Law
					</h2>
					<p className="text-gray-700">
						These Terms are governed by the laws of England and Wales. Any
						disputes arising from these Terms or the Service shall be subject to
						the exclusive jurisdiction of the courts of England and Wales.
					</p>
				</section>

				{/* 13. Changes to Terms */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						13. Changes to These Terms
					</h2>
					<p className="text-gray-700">
						We may update these Terms from time to time. We will notify you of
						significant changes via email. Continued use of the Service after
						changes constitutes acceptance of the updated Terms.
					</p>
				</section>

				{/* 14. Contact */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						14. Contact Us
					</h2>
					<p className="mb-4 text-gray-700">
						Questions about these Terms of Service?
					</p>
					<div className="rounded-lg bg-gray-50 p-6">
						<p className="text-gray-900">
							<strong>Email:</strong>{' '}
							<a
								href="mailto:hello@mybillingsmethod.com"
								className="text-purple-600 underline hover:text-purple-700"
							>
								hello@mybillingsmethod.com
							</a>
						</p>
						<p className="mt-2 text-gray-900">
							<strong>Company:</strong> CHRIDAM LTD
						</p>
						<p className="mt-2 text-gray-900">
							<strong>Location:</strong> United Kingdom
						</p>
					</div>
				</section>
			</div>

			{/* Footer */}
			<footer className="border-t border-gray-200 bg-white py-12">
				<div className="mx-auto max-w-5xl px-6">
					<div className="flex flex-col items-center justify-between gap-4 text-sm text-gray-600 md:flex-row">
						<div className="flex items-center space-x-2">
							<Icon name="lock-closed" className="h-5 w-5 text-purple-600" />
							<span className="font-semibold text-gray-900">
								MyBillingsMethod
							</span>
						</div>
						<div className="flex gap-6">
							<Link to="/privacy" className="hover:text-gray-900">
								Privacy
							</Link>
							<Link to="/tos" className="hover:text-gray-900">
								Terms
							</Link>
							<Link to="/support" className="hover:text-gray-900">
								Support
							</Link>
						</div>
					</div>
					<p className="mt-6 text-center text-xs text-gray-500">
						© {new Date().getFullYear()} MyBillingsMethod. Your data is yours.
						Always local. Always private.
					</p>
				</div>
			</footer>
		</div>
	)
}
